import React, { useEffect, useState } from 'react';
import { AccessProfile, CompanyProfile, ManagedSite, UserProfile } from '../../../services/user/userModels';

export function SiteSwitcher(props: SiteSwitcherProps) {
  const [ currentProfile, setCurrentProfile ] = useState<AccessProfile>(props.currentProfile ?? new CompanyProfile);
  const [ currentSite, setCurrentSite ] = useState<ManagedSite>(props.currentSite && new ManagedSite(props.currentSite));
  const [ currentUser, setCurrentUser ] = useState<UserProfile>(props.currentUser ?? new UserProfile);
  const [ sitesToDisplay, setSitesToDisplay ] = useState<ManagedSite[]>([]);
  const [ canSwitchCompany, setCanSwitchCompany ] = useState<boolean>(props.canSwitchCompany ?? false);
  const [ canSwitchSiteOrProfile, setCanSwitchSiteOrProfile ] = useState<boolean>(props.canSwitchSiteOrProfile ?? false);

  const getUserNavigationData = async () => {
    const response = await fetch('/user/current-user-navigation-data/' + props.siteId);
    return response.json();
  };

  const filterSitesToDisplay = (profile: AccessProfile) => {
    return profile.sites.map(site => new ManagedSite(site))
      .filter(site => site.status !== 'pending' && site.status !== 'suspended');
  };

  useEffect(() => {
    if (props.requestType === 'company') {
      getUserNavigationData().then(result => {
        setCurrentProfile(result.currentProfile);
        setCurrentUser(result.currentUser);
        setCurrentSite(new ManagedSite(result.currentSite));
        setSitesToDisplay(filterSitesToDisplay(result.currentProfile));
        setCanSwitchCompany(result.canSwitchCompany);
        setCanSwitchSiteOrProfile(result.canSwitchSiteOrProfile);
      });
    }
    if (props.requestType === 'admin') {
      setSitesToDisplay(filterSitesToDisplay(props.currentProfile));
    }
  }, []);

  return <div
    className='site-switcher-container'
    onClick={props.saveContent}
  >
    <div className='site-switcher'>
      <a
        href={
          currentProfile.type === 'Company'
            ? '/dashboard/company'
            : currentProfile.type === 'Organization'
              ? '/organization'
              : '/admin'
        }
        title='Home'
        className='button home-button'
      >
        <span className='icon has-text-white'>
          <i className='fas fa-home'></i>
        </span>
      </a>
      {
        canSwitchSiteOrProfile
          ? <>
            <h2
              className='is-flex is-justify-content-space-between subtitle has-text-white profile-switcher-head'
              tabIndex={1}
            >
              <span className='profile-switcher'>
                {
                  currentSite?.fullName ||
                  currentProfile.companyName ||
                  (
                    currentProfile.type === 'System'
                      ? 'System administrator'
                      : currentProfile.type === 'Organization'
                        ? 'VPPPA administrator'
                        : ''
                  )
                }
              </span>
              <i className='fas fa-sort-down is-pulled-right mr-1'></i>
            </h2>
            <div
              className='profile-switcher-body'
            >
              {
                currentProfile.companyName &&
                 <a href='/dashboard/company'>
                   {currentProfile?.companyName}
                 </a>
              }
              {
                sitesToDisplay.map((site) => (
                  <a
                    key={site.siteId}
                    href={`/dashboard/site/${site.siteId}`}
                    className='site-indent'
                  >
                    { site.fullName }
                  </a>
                ))
              }
              {
                canSwitchCompany &&
                 <a href='/user/switch-company'>
                   Other companies&hellip;
                 </a>
              }
              {
                currentUser.systemProfile &&
                 <a href='/user/switch-to-system'>
                  System administrator
                 </a>
              }
              {
                currentUser.organizationProfile &&
                 <a href='/user/switch-to-organization'>
                  VPPPA administrator
                 </a>
              }
            </div>
          </>
          : <h2 className='is-flex is-justify-content-space-between subtitle has-text-white profile-switcher-single'>
            <span className='profile-switcher'>
              {
                currentUser.systemProfile
                  ? 'System administrator'
                  : currentUser.organizationProfile
                    ? 'VPPPA administrator'
                    : (
                      currentSite?.fullName ?? currentProfile.companyName
                    )
              }
            </span>
          </h2>
      }
    </div>
    <div className='is-flex is-flex-direction-column is-justify-content-center is-align-items-center'>
      <div className='site-switcher-logo'>
        <img
          className='image'
          src='/images/vpppa_logo.png'
          alt='VPPPA Logo'
        />
      </div>
    </div>
    {
      currentSite?.status === 'evaluation' &&
        <div
          className='tag is-warning is-align-self-center my-2'
          title='This site is in evaluation mode. To upgrade, please contact vppsupport@auditsoft.co'
        >
          Evaluation
        </div>
    }
  </div>;
}

type AdminProps = {
  requestType: 'admin';
  currentProfile: AccessProfile;
  currentSite: ManagedSite;
  currentUser: UserProfile;
  canSwitchCompany: boolean;
  canSwitchSiteOrProfile: boolean;
  saveContent?: never;
  siteId?: never;
};

type CompanyProps = {
  requestType: 'company';
  currentProfile?: never;
  currentSite?: never;
  currentUser?: never;
  canSwitchCompany?: never;
  canSwitchSiteOrProfile?: never;
  saveContent: () => void;
  siteId: number;
};

type SiteSwitcherProps = AdminProps | CompanyProps;